import React from "react";
import { Slider } from "@mui/material";
import { PlayerEventBus } from "../../../playerEventBus";
import { styled } from "@mui/material/styles";
import { usePlayerSelector } from "../../../../../../store/slices/player/playerSlice";
import {convertSecondsToTime} from "../../../utils";

import styles from "./ProgressSlider.module.scss";

export interface ProgressSliderProps {
    getPlayerCurrentTime: () => number;
    isFullScreen: boolean;
    className?: string;
}

const CustomSlider = styled(Slider, {
    shouldForwardProp: (prop) => prop !== 'hasTransition',
})<{ hasTransition: boolean }>(({ hasTransition }) => ({
    '& .MuiSlider-rail': {
        transition: hasTransition ? 'width 2s' : 'none',
        backgroundColor: 'var(--mui-palette-divider)',
        opacity: 1,
    },
    '& .MuiSlider-track': {
        transition: hasTransition ? 'width 2s' : 'none',
        border: 'none',
    },
    '& .MuiSlider-thumb': {
        opacity: 0,
        backgroundColor: '#ffffff',
        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.4)',
    },
    '&:hover .MuiSlider-thumb, &:active .MuiSlider-thumb': {
        opacity: 1,
    },
}));

export const ProgressSlider: React.FC<ProgressSliderProps> = ({getPlayerCurrentTime, isFullScreen, className}) => {
    const { status, totalDuration } = usePlayerSelector(['status', 'totalDuration']);
    const [progress, setProgress] = React.useState<number>(0);
    const [timePassed, setTimePassed] = React.useState<number>(0);
    const [isInteracting, setIsInteracting] = React.useState<boolean>(false);

    const progressInterval = React.useRef<NodeJS.Timer | null>(null);

    const handleChanged = (value: number) => {
        setProgress(value);
        const time = totalDuration ? (value / 100) * totalDuration : 0;
        setTimePassed(time);
        PlayerEventBus.emit('seek', time);
    };

    const activateInterval = React.useCallback(() => {
        if (progressInterval.current) {
            clearInterval(progressInterval.current);
            setTimePassed(getPlayerCurrentTime());
        }

        progressInterval.current = setInterval(() => {
            if (totalDuration) {
                setTimePassed(prev => {
                    return prev + 1;
                });
                setProgress((prevProgress) => {
                    const updatedProgress = prevProgress + (100 / totalDuration);
                    if (updatedProgress >= 100) {
                        clearInterval(progressInterval.current!);
                        progressInterval.current = null;
                    }
                    return updatedProgress > 100 ? 100 : updatedProgress;
                });
            }
        }, 1000);
    }, [totalDuration, getPlayerCurrentTime]);

    const handlePlay = React.useCallback(() => {
        activateInterval();
    }, [activateInterval]);

    const handlePause = () => {
        if (progressInterval.current) {
            clearInterval(progressInterval.current);
            progressInterval.current = null;
        }
    };

    const handleMouseUp = () => {
        if (isInteracting) {
            setIsInteracting(false);
            handleChanged(progress); // Update progress when interaction ends
        }
    };

    const handleTouchEnd = () => {
        if (isInteracting) {
            setIsInteracting(false);
            handleChanged(progress); // Update progress when interaction ends
        }
    };

    React.useEffect(() => {
        switch (status) {
            case 'Unstarted':
                setProgress(0);
                break;
            case 'Playing':
                handlePlay();
                break;
            case 'Paused':
                handlePause();
                break;
        }
    }, [handlePlay, status]);

    return (
        <div className={`${styles.slider__container} ${className}`}>
            <div className={styles.slider__time}>{convertSecondsToTime(timePassed)}</div>
            <CustomSlider
                hasTransition={status !== 'Buffering' && !isInteracting}
                size="small"
                onChange={(_e, value) => {
                    if (!isInteracting) {
                        setIsInteracting(true); // Mark as interacting
                    }
                    setProgress(value as number); // Update progress while interacting
                }}
                onMouseUp={handleMouseUp}  // Trigger update when mouse interaction ends
                onTouchEnd={handleTouchEnd}  // Trigger update when touch interaction ends
                sx={{ color: 'primary.main', height: 3 }}
                value={progress}
                valueLabelDisplay="off"
            />
            <div className={styles.slider__time}>{convertSecondsToTime(totalDuration)}</div>
        </div>
    );
};
